import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import {
  getBatchUploadBreakdownItems,
  getBatchUploadRowAction,
  getBatchUploadValidBreakdown,
} from "../../utils/batchUploadSummary";
import {
  BatchUploadRowStatusBadge,
  BatchUploadSummaryItems,
} from "./BatchUploadBadges";

const DATE_KEYS = new Set(["_reportingDate", "entryDate"]);

const formatCell = (value, key) => {
  if (value == null || value === "") return "";
  if (DATE_KEYS.has(key)) {
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return value;
};

// Maps a preview column to the entry field holding its pre-upload value, so
// an "update" row can render "old → new" instead of just the new value.
const PREVIOUS_VALUE_KEYS = {
  _reportingDate: "_previousReportingDate",
  label: "_previousLabel",
  entryDate: "_previousEntryDate",
  value: "_previousValue",
  remark: "_previousRemark",
  unit: "_previousUnit",
};

// Value/Unit/Remark can legitimately be blank (either Value or Remark is
// required, not both), so a diff on either side of the arrow may need a
// placeholder instead of empty space, custom per field per product spec.
const MISSING_PLACEHOLDER_BY_KEY = {
  value: "[N/A]",
  unit: "[N/A]",
  remark: "[No Remark]",
};

const PREVIEW_COLUMNS = [
  { key: "_orgCode", label: "Org" },
  { key: "_disclosureCode", label: "Metric" },
  { key: "_reportingDate", label: "Reporting Date" },
  { key: "label", label: "Entry Name" },
  { key: "entryDate", label: "Entry Date" },
  { key: "value", label: "Value" },
  { key: "remark", label: "Remark" },
  { key: "unit", label: "Unit" },
  { key: "year", label: "Year" },
  { key: "category", label: "Category" },
  { key: "denominator", label: "Denominator" },
];

const SheetPreviewTable = ({ sheet }) => {
  const { t } = useTran();
  const { sheetName, validRows, entries, errors } = sheet;
  const validBreakdown = getBatchUploadValidBreakdown({
    totalValid: validRows,
    newRows: sheet.newRows,
    updatedRows: sheet.updatedRows,
    skippedRows: sheet.skippedRows ?? sheet.unchangedRows,
  });
  const breakdownItems = getBatchUploadBreakdownItems(validBreakdown);

  if (validRows === 0 && errors.length === 0) return null;

  const visibleCols = PREVIEW_COLUMNS.filter((col) => {
    const previousKey = PREVIOUS_VALUE_KEYS[col.key];
    return entries.some((e) => {
      const hasNew = e[col.key] != null && e[col.key] !== "";
      const hasPrevious =
        previousKey && e[previousKey] != null && e[previousKey] !== "";
      return hasNew || hasPrevious;
    });
  });

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
        <span className="text-sm font-medium">{sheetName}</span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {validBreakdown.hasBreakdown ? (
            <BatchUploadSummaryItems items={breakdownItems} variant="badge" />
          ) : validRows > 0 ? (
            <Badge
              variant="secondary"
              className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            >
              {validRows} {t("valid")}
            </Badge>
          ) : null}
          {errors.length > 0 && (
            <Badge variant="destructive">
              {errors.length} {t("errors")}
            </Badge>
          )}
        </div>
      </div>

      {entries.length > 0 && (
        <div className="border-t border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">
                  {t("Status")}
                </th>
                {visibleCols.map((col) => (
                  <th
                    key={col.key}
                    className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap"
                  >
                    {t(col.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => {
                const action = getBatchUploadRowAction(entry);
                return (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <BatchUploadRowStatusBadge action={action} />
                    </td>
                    {visibleCols.map((col) => {
                      const newValue = formatCell(entry[col.key], col.key);
                      const previousKey = PREVIOUS_VALUE_KEYS[col.key];
                      const previousValue = previousKey
                        ? formatCell(entry[previousKey], col.key)
                        : null;
                      const placeholder = MISSING_PLACEHOLDER_BY_KEY[col.key];
                      const previousMissing = previousValue === "";
                      const newMissing = newValue === "";

                      // Fields without a custom placeholder (label, dates)
                      // only show a diff when there was a real prior value;
                      // value/unit/remark can go missing on either side, so
                      // they diff even when one side is blank.
                      const hasChange =
                        action === "update" &&
                        previousKey &&
                        previousValue !== newValue &&
                        (placeholder
                          ? !previousMissing || !newMissing
                          : !previousMissing);

                      const displayPrevious =
                        placeholder && previousMissing
                          ? placeholder
                          : previousValue;
                      const displayNew =
                        placeholder && newMissing ? placeholder : newValue;

                      return (
                        <td
                          key={col.key}
                          className="px-2 py-1.5 whitespace-nowrap max-w-[200px] truncate"
                        >
                          {hasChange ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-muted-foreground line-through">
                                {displayPrevious}
                              </span>
                              <span aria-hidden="true">→</span>
                              <span className="font-medium">{displayNew}</span>
                            </span>
                          ) : (
                            newValue
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {errors.length > 0 && (
        <div className="border-t border-border px-3 py-2 space-y-1">
          {errors.map((err, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-xs text-destructive"
            >
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              <span>
                {t("Row")} {err.row}: {err.field}: {err.message}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SheetPreviewTable;
